import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  RESPIRATORY_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  GASTROINTESTINAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";
import { prestoCOPDhmioScreeningQuestions } from "./PrestoCOPDhmioScreener";

/**
 * Pre-screening questions for Presto COPD Study (HMIO variant C)
 * Trial ID: VCOPDD7860C00006
 *
 * This variant splits the checkbox grid questions (Q12-Q15) into
 * individual radio questions, one per condition.
 * Q1-Q11 and Q16 remain unchanged from the base HMIO screener.
 */

// Q1-Q11 unchanged from base
const baseQuestionsStart = prestoCOPDhmioScreeningQuestions.slice(0, 11);

// Q16 (concurrent_trial) unchanged from base (index 15)
const concurrentTrialQuestion = prestoCOPDhmioScreeningQuestions[15];

// --- Respiratory conditions (split from Q12) ---

const respiratoryConditionQuestions: PreScreeningQuestion[] = [
  {
    id: "asthma",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with asthma? (not including childhood asthma)",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "bronchiectasis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with bronchiectasis?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 24226, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 24226, booleanAttributeValue: false }],
    }),
  },
  {
    id: "interstitial_lung_disease",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with interstitial lung disease (ILD) or lung fibrosis?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 25661, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 25661, booleanAttributeValue: false }],
    }),
  },
  {
    id: "cystic_fibrosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with cystic fibrosis (CF)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "alpha1_antitrypsin",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with alpha-1 antitrypsin deficiency?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "primary_ciliary_dyskinesia",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with primary ciliary dyskinesia?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "lung_resection",
    type: QuestionType.RADIO,
    question: "Have you had a surgical removal of lung tissue (lung resection)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "high_oxygen_therapy",
    type: QuestionType.RADIO,
    question: "Are you currently receiving oxygen therapy with a concentrator setting of 4 or higher?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
];

// --- Cardiovascular conditions (split from Q13) ---

const cardiovascularConditionQuestions: PreScreeningQuestion[] = [
  {
    id: "heart_failure",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with severe heart failure?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false }],
    }),
  },
  {
    id: "heart_attack_recent",
    type: QuestionType.RADIO,
    question: "Have you had a heart attack (myocardial infarction) in the past 6 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false }],
    }),
  },
  {
    id: "stroke_recent",
    type: QuestionType.RADIO,
    question: "Have you had a stroke, mini-stroke, or transient ischemic attack (TIA) in the past 6 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "angina_hospitalization",
    type: QuestionType.RADIO,
    question: "Have you been hospitalized due to angina or heart failure in the past 6 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "coronary_revascularization",
    type: QuestionType.RADIO,
    question: "Have you had coronary artery disease or a coronary revascularization (e.g., stent, bypass) in the past 6 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false }],
    }),
  },
  {
    id: "cardiomyopathy",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with cardiomyopathy (heart muscle disease)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23518, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 23518, booleanAttributeValue: false }],
    }),
  },
  {
    id: "pulmonary_hypertension",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with pulmonary hypertension (high blood pressure in the lungs)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 8249, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 8249, booleanAttributeValue: false }],
    }),
  },
];

// --- Exclusionary conditions (split from Q14) ---

const exclusionaryConditionQuestions: PreScreeningQuestion[] = [
  {
    id: "ulcerative_colitis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with ulcerative colitis (UC)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_ULCERATIVE_COLITIS, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_ULCERATIVE_COLITIS, booleanAttributeValue: false }],
    }),
  },
  {
    id: "crohns_disease",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with Crohn's disease?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_CROHNS, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_CROHNS, booleanAttributeValue: false }],
    }),
  },
  {
    id: "liver_failure",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with liver failure or cirrhosis?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false }],
    }),
  },
  {
    id: "eskd",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with end-stage kidney disease (ESKD) or are you currently on dialysis?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false }],
    }),
  },
  {
    id: "hepatitis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with hepatitis B or C?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "hiv",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with HIV?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false }],
    }),
  },
  {
    id: "active_tb",
    type: QuestionType.RADIO,
    question: "Do you have active tuberculosis (TB) or are you currently on TB treatment?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: false }],
    }),
  },
  {
    id: "dementia",
    type: QuestionType.RADIO,
    question: "Have you been formally diagnosed with dementia?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23896, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 23896, booleanAttributeValue: false }],
    }),
  },
];

// --- Immunosuppression / transplant conditions (split from Q15) ---

const immunosuppressionTransplantQuestions: PreScreeningQuestion[] = [
  {
    id: "organ_transplant",
    type: QuestionType.RADIO,
    question: "Do you have a history of organ transplant or are you currently on the transplant waitlist?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 15039, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 15039, booleanAttributeValue: false }],
    }),
  },
  {
    id: "immunosuppressive_medications",
    type: QuestionType.RADIO,
    question: "Do you currently take immunosuppressant medications?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "cancer_recent",
    type: QuestionType.RADIO,
    question: "Have you had cancer within the past 5 years? (excluding non-melanoma skin cancers such as basal cell or squamous cell carcinoma)",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
];

// --- Assemble the full variant C question set ---

export const prestoCOPDhmioScreenerCQuestions: PreScreeningQuestion[] = [
  ...baseQuestionsStart,              // Q1-Q11 (indices 0-10)
  ...respiratoryConditionQuestions,    // 8 individual radio questions (was Q12)
  ...cardiovascularConditionQuestions, // 7 individual radio questions (was Q13)
  ...exclusionaryConditionQuestions,   // 8 individual radio questions (was Q14)
  ...immunosuppressionTransplantQuestions, // 3 individual radio questions (was Q15)
  concurrentTrialQuestion,            // Q16 unchanged
];
